import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { getDashboardPath } from "../../../utils/authRedirect";
import { buildClinicPortalUrl } from "../../../utils/portalHost";
import { hasAdminAccess } from "../../../utils/roles";
import platformAPI from "../../../services/platformApi";
import L from "../../../constants/baysportLabels";
import { TRIAL_DAYS } from "../../../constants/trial";
import {
  buildLandingPriceHighlights,
  PLAN_POSITIONING,
  LANDING_PLANS_FALLBACK,
} from "../../../constants/planMarketing";
import LandingNav from "./LandingNav";
import { LegalFooterLinks } from "../../Legal/LegalDocLink";
import LandingStorySections from "./LandingStorySections";
import LandingAllInOneSections from "./LandingAllInOneSections";
import LandingPhoneFrame from "./LandingPhoneFrame";
import { LANDING_SCREEN } from "./landingAssets";
import { LANDING_HERO_SCREEN } from "./landingStoryContent";
import {
  AIO_HERO,
  AIO_FAQ,
  AIO_PRICING,
  AIO_FINAL_CTA,
} from "./landingAllInOneContent";
import { trackTryClick } from "../../../analytics/metrikaGoals";
import { yandexMetrikaParams } from "../../../analytics/yandexMetrika";
import {
  LANDING_PAGE_TITLE,
  LANDING_PAGE_DESCRIPTION,
  LANDING_PAGE_KEYWORDS,
  LANDING_OG_IMAGE,
  LANDING_OG_IMAGE_ALT,
  buildLandingJsonLd,
  APP_LANDING_PAGE_TITLE,
  APP_LANDING_PAGE_DESCRIPTION,
  APP_LANDING_PAGE_KEYWORDS,
  buildAppLandingJsonLd,
  CRM_LANDING_PAGE_TITLE,
  CRM_LANDING_PAGE_DESCRIPTION,
  CRM_LANDING_PAGE_KEYWORDS,
  buildCrmLandingJsonLd,
} from "./landingSeo";
import { getAdLanding } from "./landingAdPages";
import { isHomeLandingV2Enabled } from "../../../utils/productFlags";
import Seo from "../../Seo";
import s from "./homeLanding.module.css";

const USP_ITEMS = [
  {
    badge: `Только у ${L.productName}`,
    icon: "🌐",
    title: "Соцсеть участников",
    text: "Профили спортсменов, подписки, стена достижений и лента клуба — клиенты возвращаются сами, а не только из-за расписания.",
    contrast: "Обычные CRM дают только таблицу и SMS.",
  },
  {
    badge: "Ваш бренд",
    icon: "🎨",
    title: "Персонализация портала",
    text: "Логотип, цвета, обложка, свой splash при запуске PWA. Родители видят ваш клуб — не чужой сервис.",
    contrast: "С первого экрана — логотип и цвета вашей секции.",
  },
  {
    badge: "Без сложностей",
    icon: "⚡",
    title: "CRM, которая не мешает",
    text: "Журнал в один клик, абонементы, оплаты и аналитика — всё рядом. Настройка за вечер, не за месяц.",
    contrast: "Не нужны интеграторы и обучение команды неделями.",
  },
];

const FEATURE_HIGHLIGHTS = [
  {
    icon: "📅",
    title: "Календарь и журнал",
    text: "Расписание групп, отметка посещаемости и списание с абонемента в одно касание.",
  },
  {
    icon: "🧒",
    title: "Клиенты и абонементы",
    text: "Карточки спортсменов, типы абонементов, оплаты и контроль должников.",
  },
  {
    icon: "📰",
    title: "Лента клуба",
    text: "Новости, фото с тренировок, опросы — публикуйте прямо из кабинета администратора.",
  },
  {
    icon: "📊",
    title: "Аналитика клуба",
    text: "Выручка, посещаемость, рейтинг групп — решения на основе цифр.",
  },
  {
    icon: "📱",
    title: "PWA на телефоне",
    text: "Клиенты ставят приложение клуба на главный экран — без App Store и Google Play.",
  },
  {
    icon: "🏋️",
    title: "Кабинет тренера",
    text: "Отдельный портал для тренеров с расписанием, журналом и группами.",
  },
];

const STEPS = [
  {
    num: "1",
    title: "5 минут — и клуб в системе",
    text: `Регистрация, название, логотип. ${TRIAL_DAYS} дней «Максимального» на этот клуб — без карты.`,
  },
  {
    num: "2",
    title: "Добавьте группы и абонементы",
    text: "Готовые шаблоны под секции и единоборства. Пригласите клиентов по ссылке или QR.",
  },
  {
    num: "3",
    title: "Клиенты уже в кабинете",
    text: "Расписание, лента, оплаты и соцпрофиль — всё в одном приложении на телефоне.",
  },
];

const APP_USP_ITEMS = [
  {
    badge: "Приложение клуба",
    icon: "📱",
    title: "PWA для клиентов",
    text: "Приложение клуба без App Store: отметка посещаемости, лента и подписки в одном месте.",
    contrast: "Обычные CRM не становятся привычным приложением для учеников.",
  },
  {
    badge: "Ваш бренд",
    icon: "🎨",
    title: "Брендирование портала",
    text: "Логотип, цвета, обложки и splash — чтобы ваш клуб узнавали с первого запуска.",
    contrast: "Кастомизация у «сервиса-контейнера» обычно ограничена.",
  },
  {
    badge: `Только у ${L.productName}`,
    icon: "⚡",
    title: "CRM, которая ведёт клуб",
    text: "Журнал, абонементы, оплаты и аналитика — в связке с приложением на телефоне.",
    contrast: "Сначала настройка, потом разрозненные отчёты — так дороже и сложнее.",
  },
];

const CRM_USP_ITEMS = [
  {
    badge: "Журнал и абонементы",
    icon: "📅",
    title: "Управление секцией в CRM",
    text: "Расписание групп, посещаемость, абонементы, должники и списания — без таблиц.",
    contrast: "«Экосистемы» без реального журнала не решают ежедневные задачи.",
  },
  {
    badge: "Финансы и аналитика",
    icon: "📊",
    title: "Цифры клуба под рукой",
    text: "Выручка, посещаемость, рейтинг групп — чтобы принимать решения быстрее.",
    contrast: "SMS и выгрузки не заменяют аналитику внутри системы.",
  },
  {
    badge: `Только у ${L.productName}`,
    icon: "🌐",
    title: "Соцсеть сверху",
    text: "Лента, новости и профиль клуба повышают удержание — и клиенты возвращаются.",
    contrast: "CRM без вовлечения обычно даёт отток, а не комьюнити.",
  },
];

const APP_FEATURE_HIGHLIGHTS = [
  {
    icon: "📱",
    title: "PWA на телефоне",
    text: "Клиенты ставят приложение клуба на главный экран — без App Store и Google Play.",
  },
  {
    icon: "📰",
    title: "Лента клуба",
    text: "Новости, фото и опросы — публикуйте прямо из кабинета администратора.",
  },
  {
    icon: "🏋️",
    title: "Кабинет тренера",
    text: "Отдельный портал для тренеров с расписанием, журналом и группами.",
  },
  {
    icon: "📅",
    title: "Календарь и журнал",
    text: "Расписание, отметка посещаемости и списание с абонемента в одно касание.",
  },
  {
    icon: "📊",
    title: "Аналитика клуба",
    text: "Выручка, посещаемость, рейтинг групп — решения на основе цифр.",
  },
];

const CRM_FEATURE_HIGHLIGHTS = [
  {
    icon: "📅",
    title: "Календарь и журнал",
    text: "Расписание групп, отметка посещаемости и списание с абонемента в одно касание.",
  },
  {
    icon: "🧒",
    title: "Клиенты и абонементы",
    text: "Карточки спортсменов, типы абонементов, оплаты и контроль должников.",
  },
  {
    icon: "📊",
    title: "Аналитика клуба",
    text: "Выручка, посещаемость, рейтинг групп — решения на основе цифр.",
  },
  {
    icon: "📰",
    title: "Лента клуба",
    text: "Новости и достижения — встроенная соцсеть повышает вовлечённость.",
  },
  {
    icon: "🎨",
    title: "Брендирование портала",
    text: "Логотип и персонализация под ваш клуб — чтобы CRM выглядела как ваш продукт.",
  },
];

const APP_STEPS = [
  {
    num: "1",
    title: "Запустите приложение клуба за 5 минут",
    text: `Регистрация, название, логотип. ${TRIAL_DAYS} дней «Максимального» на этот клуб — без карты.`,
  },
  {
    num: "2",
    title: "Добавьте группы и абонементы",
    text: "Готовые шаблоны под секции и единоборства. Пригласите клиентов по ссылке или QR.",
  },
  {
    num: "3",
    title: "Клиенты получают приложение",
    text: "Лента, подписки, оплаты и уведомления — на телефоне ученика.",
  },
];

const CRM_STEPS = [
  {
    num: "1",
    title: "Включите CRM для спорта",
    text: `Регистрация, название и логотип. ${TRIAL_DAYS} дней «Максимального» на этот клуб — без карты.`,
  },
  {
    num: "2",
    title: "Настройте журнал и абонементы",
    text: "Расписание групп, типы абонементов и правила оплаты — без долгой интеграции.",
  },
  {
    num: "3",
    title: "Управляйте секцией из кабинета",
    text: "От тренеров до администратора: посещаемость, финансы и аналитика в одном интерфейсе.",
  },
];

function formatRub(n) {
  return new Intl.NumberFormat("ru-RU").format(Number(n) || 0);
}

function isMarkIcon(icon) {
  return /^\d{2}$/.test(String(icon || ""));
}

function LandingFaq({ items, title = "Частые вопросы" }) {
  const [openQ, setOpenQ] = useState(items?.[0]?.q || null);
  if (!items?.length) return null;
  return (
    <section
      id="faq"
      className={`${s.section} ${s.sectionFaq}`}
      aria-labelledby="landing-faq-title"
    >
      <div className={s.sectionHead}>
        <h2 id="landing-faq-title" className={s.sectionTitle}>
          {title}
        </h2>
      </div>
      <div className={s.faqList}>
        {items.map((item) => (
          <details
            key={item.q}
            className={s.faqItem}
            open={openQ === item.q}
            onToggle={(event) => {
              if (event.currentTarget.open) {
                setOpenQ(item.q);
                return;
              }
              setOpenQ((current) => (current === item.q ? null : current));
            }}
          >
            <summary className={s.faqQ}>
              <span>{item.q}</span>
              <span className={s.faqPlus} aria-hidden />
            </summary>
            <p className={s.faqA}>{item.a}</p>
          </details>
        ))}
      </div>
    </section>
  );
}

export default function HomeLanding({ variant = "home" } = {}) {
  const { isAuth, role, tenant } = useSelector((state) => state.auth);
  const hasClub = isAuth && tenant?.subdomain && hasAdminAccess(role);
  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(true);
  const ad = getAdLanding(variant);
  const useAllInOneHome =
    !ad && variant === "home" && isHomeLandingV2Enabled();
  const landingKey =
    ad?.metrika ||
    (variant === "crm" ? "crm" : variant === "app" ? "app_legacy" : "home");

  useEffect(() => {
    yandexMetrikaParams({ landing: landingKey });
  }, [landingKey]);

  const onTryClick = () => {
    trackTryClick({ landing: landingKey });
  };

  const variantSeo = ad
    ? ad.seo
    : variant === "app"
      ? {
          title: APP_LANDING_PAGE_TITLE,
          description: APP_LANDING_PAGE_DESCRIPTION,
          keywords: APP_LANDING_PAGE_KEYWORDS,
          jsonLdBuilder: buildAppLandingJsonLd,
        }
      : variant === "crm"
        ? {
            title: CRM_LANDING_PAGE_TITLE,
            description: CRM_LANDING_PAGE_DESCRIPTION,
            keywords: CRM_LANDING_PAGE_KEYWORDS,
            jsonLdBuilder: buildCrmLandingJsonLd,
          }
        : {
            title: LANDING_PAGE_TITLE,
            description: LANDING_PAGE_DESCRIPTION,
            keywords: LANDING_PAGE_KEYWORDS,
            jsonLdBuilder: buildLandingJsonLd,
          };

  const uspItems = ad
    ? ad.uspItems
    : variant === "app"
      ? APP_USP_ITEMS
      : variant === "crm"
        ? CRM_USP_ITEMS
        : USP_ITEMS;
  const featureHighlights = ad
    ? ad.featureHighlights
    : variant === "app"
      ? APP_FEATURE_HIGHLIGHTS
      : variant === "crm"
        ? CRM_FEATURE_HIGHLIGHTS
        : FEATURE_HIGHLIGHTS;
  const steps = ad
    ? ad.steps
    : variant === "app"
      ? APP_STEPS
      : variant === "crm"
        ? CRM_STEPS
        : STEPS;

  const heroConfig = ad
    ? ad.hero
    : variant === "app"
      ? {
            eyebrow: `Своё приложение клуба · PWA · ${TRIAL_DAYS} дней бесплатно`,
            h1Line1: "Приложение клуба",
            h1Line2: "в телефоне клиента",
            titleAccent: "живет в телефоне клиента",
          lead:
              "Запустите приложение спортивного клуба без разработчиков: расписание, лента, чат, профиль спортсмена и CRM клуба в одной системе под вашим брендом.",
            primaryCta: "Запустить приложение клуба",
            uspTitle: "Не просто CRM, а приложение, куда клиент реально заходит",
          uspLead:
              "Когда у клуба есть своё приложение, расписание, новости, чат и абонемент живут в одном месте. Не в мессенджерах, не в таблицах и не в чужом сервисе.",
            featuresTitle: "Что получает клуб вместе с приложением",
          featuresLead:
              "Снаружи это выглядит как удобное приложение для клиента. Внутри это полноценная система управления клубом.",
          stepsTitle: "Три шага — и у клуба появилось своё приложение",
          stepsLead:
            "Вы настраиваете секции и абонементы, а клиенты уже видят расписание, ленту и чат в телефоне — без разработчиков и интеграторов.",
          pricingTitle: `Тарифы для клубов с приложением`,
          pricingLead:
            `Тариф выбираете для каждого клуба: от стартового до максимального. ${TRIAL_DAYS} дней “Максимального” бесплатно при регистрации — платите только за то, чем пользуетесь.`,
        }
      : variant === "crm"
        ? {
            eyebrow: `CRM для спорта · журнал · абонементы · ${TRIAL_DAYS} дней бесплатно`,
            h1Line1: "CRM для секции",
            h1Line2: "а не набор таблиц",
            titleAccent: "CRM для секции, а не набор таблиц",
            lead:
              "Baysport помогает вести клуб в одной системе: клиенты и группы, абонементы и журнал, финансы, тренеры и аналитика. Без Excel, без хаоса и без долгого внедрения.",
            primaryCta: "Попробовать CRM для клуба",
            uspTitle: "CRM, в которой удобно работать каждый день",
            uspLead:
              "Администратору нужен контроль, тренеру нужен быстрый журнал, владельцу нужны цифры. Здесь это собрано в одном кабинете и не разваливается на десять сервисов.",
            featuresTitle: "Что входит в CRM для спортивного клуба",
            featuresLead:
              "Вся операционка клуба в одном месте: от посещаемости и расписания до оплаты и выручки.",
            stepsTitle: "Три шага — и CRM начинает работать",
            stepsLead:
              "Журнал, абонементы, финансы — в одном процессе. Без долгого внедрения: подключили клуб, добавили группы и работаете уже сегодня.",
            pricingTitle: `Тарифы CRM для спорта`,
            pricingLead:
              `Тариф выбираете для каждого клуба отдельно: от одного тренера до крупной секции. ${TRIAL_DAYS} дней “Максимального” бесплатно — чтобы вы спокойно запустили процессы и посчитали эффективность.`,
          }
        : useAllInOneHome
          ? {
              h1Line1: AIO_HERO.h1Before,
              h1Line2: AIO_HERO.h1Highlight,
              lead: AIO_HERO.lead,
              primaryCta: AIO_HERO.primaryCta,
              secondaryCta: AIO_HERO.secondaryCta,
              trialNote: AIO_HERO.trialNote,
              pricingTitle: AIO_PRICING.title,
              pricingLead: AIO_PRICING.lead,
            }
          : {
              eyebrow: `Соцсеть + CRM · PWA · ${TRIAL_DAYS} дней бесплатно`,
              h1Line1: "Клуб, который",
              h1Line2: "живёт в телефоне клиента",
              titleAccent: "живёт в телефоне клиента",
              lead: `${L.productName} — единственная платформа для спортивных секций со встроенной соцсетью участников, персонализацией под ваш бренд и привычной CRM: журнал, абонементы, финансы.`,
              primaryCta: `Попробовать ${TRIAL_DAYS} дней бесплатно`,
              uspTitle: "Чего нет у обычных CRM",
              uspLead: `Большинство систем — это таблицы и SMS. ${L.productName} даёт клубу живое сообщество и узнаваемый бренд. При этом всё настраивается за вечер.`,
              featuresTitle: "И вся CRM под капотом",
              featuresLead:
                "Соцсеть и брендинг — сверху. Под ними полноценное управление: от одиночного тренера до секции с командой.",
              stepsTitle: "Три шага — и всё работает",
              stepsLead:
                "Не нужен IT-отдел. Зарегистрировались, добавили группы — клиенты уже в кабинете на телефоне.",
              pricingTitle: `Тарифы ${L.productName}`,
              pricingLead: `Тариф выбираете для каждого клуба отдельно: от стартового для одного тренера до максимального для крупной секции. ${TRIAL_DAYS} дней «Максимального» бесплатно при регистрации клуба. Создавайте сколько угодно организаций — платите только за те, что используете.`,
            };

  const storySectionIds = ad
    ? ad.storySectionIds
    : variant === "app"
      ? ["social", "branding"]
      : variant === "crm"
        ? ["club"]
        : ["branding", "social", "manage"];

  useEffect(() => {
    platformAPI
      .getPublicPlans()
      .then((r) => {
        const loaded = Array.isArray(r?.plans) ? r.plans : [];
        setPlans(loaded.length ? loaded : LANDING_PLANS_FALLBACK);
      })
      .catch(() => setPlans(LANDING_PLANS_FALLBACK))
      .finally(() => setPlansLoading(false));
  }, []);

  const featuredCode = useMemo(() => {
    if (ad?.featuredPlanCode) return ad.featuredPlanCode;
    const withBadge = plans.find((p) => p.badge);
    return withBadge?.code || plans[1]?.code || plans[0]?.code;
  }, [plans, ad]);

  return (
    <div
      className={`${s.page} ${s.pageLanding} ${
        useAllInOneHome ? s.pageAioLight : ""
      } ${variant === "app" ? s.pageApp : ""}`}
    >
      <Seo
        title={variantSeo.title}
        description={variantSeo.description}
        keywords={variantSeo.keywords}
        ogImage={LANDING_OG_IMAGE}
        ogImageAlt={LANDING_OG_IMAGE_ALT}
        jsonLd={variantSeo.jsonLdBuilder()}
      />
      {hasClub ? (
        <div className={s.sessionBar}>
          <span>
            Вы вошли в <strong>{tenant.name || tenant.subdomain}</strong>
          </span>
          <div className={s.sessionActions}>
            <a
              href={buildClinicPortalUrl(
                tenant.subdomain,
                getDashboardPath(role),
              )}
              className={`${s.btn} ${s.btnPrimary}`}
            >
              Открыть кабинет
            </a>
            <Link to="/register-clinic?new=1" className={`${s.btn} ${s.btnGhost}`}>
              Новый клуб
            </Link>
          </div>
        </div>
      ) : null}

      <LandingNav
        mode="platform"
        onTryClick={onTryClick}
        showProductLinks={useAllInOneHome}
        tryCtaLabel={
          useAllInOneHome ? `Попробовать ${TRIAL_DAYS} дней` : undefined
        }
      />

      <div className={useAllInOneHome ? s.aioHeroBand : undefined}>
        <section
          className={`${s.hero} ${
            useAllInOneHome ? `${s.aioHero} ${s.aioHeroStagger}` : ""
          }`}
        >
          <div className={s.heroContent}>
            {useAllInOneHome ? null : (
              <div className={s.eyebrow}>{heroConfig.eyebrow}</div>
            )}
            <h1 className={useAllInOneHome ? s.aioH1 : s.title}>
              {useAllInOneHome ? (
                <>
                  {heroConfig.h1Line1}{" "}
                  <i className={s.aioH1Mark}>{heroConfig.h1Line2}</i>
                </>
              ) : (
                <>
                  {heroConfig.h1Line1}
                  <br />
                  <span className={s.titleAccent}>{heroConfig.h1Line2}</span>
                </>
              )}
            </h1>
            <p className={useAllInOneHome ? s.aioHeroLead : s.lead}>
              {heroConfig.lead}
            </p>
            <div className={s.heroActions}>
              <Link
                to="/register-clinic"
                className={
                  useAllInOneHome
                    ? s.aioPillCta
                    : `${s.btn} ${s.btnPrimary} ${s.btnLg}`
                }
                onClick={onTryClick}
              >
                {useAllInOneHome ? (
                  <span>{heroConfig.primaryCta}</span>
                ) : (
                  heroConfig.primaryCta
                )}
              </Link>
              {ad?.hideFindClub ? null : useAllInOneHome ? (
                <Link to="/find" className={s.aioGhostCta}>
                  {heroConfig.secondaryCta || "Найти свой клуб"}
                </Link>
              ) : (
                <Link to="/find" className={`${s.btn} ${s.btnOutline} ${s.btnLg}`}>
                  {heroConfig.secondaryCta || "Найти свой клуб"}
                </Link>
              )}
            </div>
            <p className={useAllInOneHome ? s.aioHeroNote : s.trialNote}>
              {heroConfig.trialNote ||
                ad?.trialNote ||
                "Регистрация за 5 минут. Каждый клуб — отдельная подписка, количество клубов не ограничено."}
            </p>
            {!useAllInOneHome ? (
              <div className={s.metrics}>
                {(ad?.metrics || [
                  { value: "5 мин", label: "до запуска клуба" },
                  { value: "1 клик", label: "отметка в журнале" },
                  { value: `0 ₽`, label: `первые ${TRIAL_DAYS} дней` },
                ]).map((metric) => (
                  <div key={metric.label} className={s.metric}>
                    <strong>{metric.value}</strong>
                    <span>{metric.label}</span>
                  </div>
                ))}
              </div>
            ) : null}
          </div>

          <div className={`${s.heroVisual} ${useAllInOneHome ? s.aioHeroVisual : ""}`}>
            {useAllInOneHome ? (
              <div className={s.aioHeroStage}>
                <img
                  className={s.aioHeroPhoneMain}
                  src={AIO_HERO.screen.src}
                  alt={AIO_HERO.screen.alt}
                  loading="eager"
                  decoding="async"
                  fetchpriority="high"
                />
                <img
                  className={s.aioHeroPhoneSide}
                  src={AIO_HERO.screenSecondary.src}
                  alt={AIO_HERO.screenSecondary.alt}
                  loading="lazy"
                  decoding="async"
                />
              </div>
            ) : (
              <>
                <div className={s.heroShotGlow} />
                {ad?.heroFrame === "desktop" ? (
                  <div className={s.heroDesktop}>
                    <div className={s.heroDesktopInner}>
                      <img
                        src={ad.heroScreen.src}
                        alt={ad.heroScreen.alt}
                        loading="eager"
                        decoding="async"
                        fetchpriority="high"
                      />
                    </div>
                  </div>
                ) : ad ? (
                  <LandingPhoneFrame
                    src={ad.heroScreen.src}
                    alt={ad.heroScreen.alt}
                    size="lg"
                    className={s.heroDevice}
                    priority
                  />
                ) : variant === "app" ? (
                  <LandingPhoneFrame
                    src={LANDING_SCREEN.clientHome}
                    alt="Приложение клиента: главная"
                    size="lg"
                    className={s.heroDevice}
                    priority
                  />
                ) : (
                  <LandingPhoneFrame
                    src={LANDING_HERO_SCREEN.src}
                    alt={LANDING_HERO_SCREEN.alt}
                    size="lg"
                    className={s.heroDevice}
                    variant="device"
                    priority
                  />
                )}
              </>
            )}
          </div>
        </section>
      </div>

      {useAllInOneHome ? (
        <LandingAllInOneSections onTryClick={onTryClick} showFinalCta={false} />
      ) : (
        <>
          <LandingStorySections sectionIds={storySectionIds} />

          {variant === "app" ? (
            <section id="features" className={`${s.section} ${s.sectionAlt}`}>
              <div className={s.sectionHead}>
                <h2 className={s.sectionTitle}>{heroConfig.featuresTitle}</h2>
                <p className={s.sectionLead}>{heroConfig.featuresLead}</p>
              </div>
              <div className={s.features}>
                {featureHighlights.map((f) => (
                  <article key={f.title} className={s.feature}>
                    <div className={`${s.featureIcon} ${isMarkIcon(f.icon) ? s.featureIconMark : ""}`}>
                      {f.icon}
                    </div>
                    <h3>{f.title}</h3>
                    <p>{f.text}</p>
                  </article>
                ))}
              </div>
            </section>
          ) : uspItems?.length ? (
            <section id="usp" className={`${s.section} ${s.sectionAlt}`}>
              <div className={s.sectionHead}>
                <h2 className={s.sectionTitle}>{heroConfig.uspTitle}</h2>
                <p className={s.sectionLead}>{heroConfig.uspLead}</p>
              </div>
              <div className={s.uspGrid}>
                {uspItems.map((item) => (
                  <article key={item.title} className={s.uspCard}>
                    <span className={s.uspBadge}>{item.badge}</span>
                    <div className={`${s.uspIcon} ${isMarkIcon(item.icon) ? s.uspIconMark : ""}`}>
                      {item.icon}
                    </div>
                    <h3 className={s.uspTitle}>{item.title}</h3>
                    <p className={s.uspText}>{item.text}</p>
                    {item.contrast ? (
                      <p className={s.uspContrast}>{item.contrast}</p>
                    ) : null}
                  </article>
                ))}
              </div>
            </section>
          ) : null}

          {ad ? (
            <section id="features" className={s.section}>
              <div className={s.sectionHead}>
                <h2 className={s.sectionTitle}>{heroConfig.featuresTitle}</h2>
                <p className={s.sectionLead}>{heroConfig.featuresLead}</p>
              </div>
              <div className={s.features}>
                {featureHighlights.map((f) => (
                  <article key={f.title} className={s.feature}>
                    <div className={`${s.featureIcon} ${isMarkIcon(f.icon) ? s.featureIconMark : ""}`}>
                      {f.icon}
                    </div>
                    <h3>{f.title}</h3>
                    <p>{f.text}</p>
                  </article>
                ))}
              </div>
            </section>
          ) : null}

          <section className={`${s.section} ${ad ? s.sectionAlt : ""}`}>
            <div className={s.sectionHead}>
              <h2 className={s.sectionTitle}>{heroConfig.stepsTitle}</h2>
              <p className={s.sectionLead}>{heroConfig.stepsLead}</p>
            </div>
            <div className={s.steps}>
              {steps.map((step) => (
                <article key={step.title} className={s.step}>
                  <div className={s.stepNum}>{step.num}</div>
                  <h3>{step.title}</h3>
                  <p className={s.stepText}>{step.text}</p>
                </article>
              ))}
            </div>
            <div className={s.stepsCta}>
              <Link
                to="/register-clinic"
                className={`${s.btn} ${s.btnPrimary}`}
                onClick={onTryClick}
              >
                {heroConfig.primaryCta || `Начать бесплатно — ${TRIAL_DAYS} дней`}
              </Link>
            </div>
          </section>

          {variant === "app" ? (
            <section id="usp" className={`${s.section} ${s.sectionAlt}`}>
              <div className={s.sectionHead}>
                <h2 className={s.sectionTitle}>{heroConfig.uspTitle}</h2>
                <p className={s.sectionLead}>{heroConfig.uspLead}</p>
              </div>
              <div className={s.uspGrid}>
                {uspItems.map((item) => (
                  <article key={item.title} className={s.uspCard}>
                    <span className={s.uspBadge}>{item.badge}</span>
                    <div className={`${s.uspIcon} ${isMarkIcon(item.icon) ? s.uspIconMark : ""}`}>
                      {item.icon}
                    </div>
                    <h3 className={s.uspTitle}>{item.title}</h3>
                    <p className={s.uspText}>{item.text}</p>
                    {item.contrast ? (
                      <p className={s.uspContrast}>{item.contrast}</p>
                    ) : null}
                  </article>
                ))}
              </div>
            </section>
          ) : ad ? null : (
            <section id="features" className={`${s.section} ${s.sectionAlt}`}>
              <div className={s.sectionHead}>
                <h2 className={s.sectionTitle}>{heroConfig.featuresTitle}</h2>
                <p className={s.sectionLead}>{heroConfig.featuresLead}</p>
              </div>
              <div className={s.features}>
                {featureHighlights.map((f) => (
                  <article key={f.title} className={s.feature}>
                    <div className={`${s.featureIcon} ${isMarkIcon(f.icon) ? s.featureIconMark : ""}`}>
                      {f.icon}
                    </div>
                    <h3>{f.title}</h3>
                    <p>{f.text}</p>
                  </article>
                ))}
              </div>
            </section>
          )}
        </>
      )}

      <section id="pricing" className={`${s.section} ${s.sectionPricing}`}>
        <div className={`${s.sectionHead} ${useAllInOneHome ? s.sectionHeadCenter : ""}`}>
          <h2 className={s.sectionTitle}>{heroConfig.pricingTitle}</h2>
          <p className={s.sectionLead}>{heroConfig.pricingLead}</p>
        </div>
        {plansLoading ? (
          <p className={s.pricingLoading}>Загрузка тарифов…</p>
        ) : (
          <div className={s.pricing}>
            {plans.map((plan) => (
              <article
                key={plan.id}
                className={`${s.priceCard} ${
                  plan.code === featuredCode ? s.priceCardFeatured : ""
                }`}
              >
                {plan.badge ? <span className={s.badge}>{plan.badge}</span> : null}
                <h3 className={s.priceName}>{plan.name}</h3>
                {PLAN_POSITIONING[plan.code] ? (
                  <p className={s.pricePositioning}>{PLAN_POSITIONING[plan.code]}</p>
                ) : null}
                <p className={s.priceAmount}>
                  {formatRub(plan.monthly_price_rub)} ₽
                  <small> / мес за клуб</small>
                </p>
                {plan.yearly_price_rub ? (
                  <p className={s.priceYearly}>
                    или {formatRub(plan.yearly_price_rub)} ₽ / год за клуб
                  </p>
                ) : null}
                <ul className={s.priceHighlights}>
                  {buildLandingPriceHighlights(plan.code).map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
                <Link
                  to="/register-clinic"
                  className={`${s.btn} ${
                    plan.code === featuredCode ? s.btnPrimary : s.btnOutline
                  }`}
                  style={{ width: "100%" }}
                  onClick={onTryClick}
                >
                  {TRIAL_DAYS} дней бесплатно
                </Link>
              </article>
            ))}
          </div>
        )}
      </section>

      <LandingFaq
        items={ad?.faq || (useAllInOneHome ? AIO_FAQ : null)}
        title={useAllInOneHome ? "Вопросы и ответы" : "Частые вопросы"}
      />

      {useAllInOneHome ? (
        <section className={s.aioFinalBand}>
          <div className={s.aioFinalBox}>
            <h2 className={s.aioSectionTitle}>{AIO_FINAL_CTA.title}</h2>
            <p className={s.aioWhyLead}>{AIO_FINAL_CTA.lead}</p>
            <div className={s.aioFinalActions}>
              <Link
                to="/register-clinic"
                className={s.aioPillCta}
                onClick={onTryClick}
              >
                <span>{AIO_FINAL_CTA.primary}</span>
              </Link>
              <Link to="/find" className={s.aioGhostCta}>
                {AIO_FINAL_CTA.secondary}
              </Link>
            </div>
            <p className={s.aioFinalNote}>
              {TRIAL_DAYS} дней бесплатно · без карты · регистрация за 5 минут
            </p>
          </div>
        </section>
      ) : null}

      {useAllInOneHome ? null : (
        <section className={s.cta}>
          <div className={s.ctaBox}>
            <h2 className={s.sectionTitle} style={{ marginBottom: 12 }}>
              {heroConfig.ctaTitle || "Клуб с характером — не с таблицей"}
            </h2>
            <p className={s.sectionLead} style={{ margin: "0 auto 24px" }}>
              {heroConfig.ctaLead ||
                `Запустите ${L.productName} за вечер: соцсеть для клиентов, ваш брендинг и CRM без головной боли. ${TRIAL_DAYS} дней «Максимального» на каждый новый клуб — бесплатно.`}
            </p>
            <div className={s.ctaActions}>
              <Link
                to="/register-clinic"
                className={`${s.btn} ${s.btnPrimary} ${s.btnLg}`}
                onClick={onTryClick}
              >
                {heroConfig.primaryCta || "Попробовать бесплатно"}
              </Link>
              {ad?.hideFindClub ? null : (
                <Link to="/find" className={`${s.btn} ${s.btnOutline} ${s.btnLg}`}>
                  Войти в клуб
                </Link>
              )}
            </div>
          </div>
        </section>
      )}

      {!hasClub ? (
        <div className={s.mobileStickyCta}>
          <Link
            to="/register-clinic"
            className={`${s.btn} ${s.btnPrimary}`}
            onClick={onTryClick}
          >
            {heroConfig.primaryCta || `${TRIAL_DAYS} дней бесплатно`}
          </Link>
        </div>
      ) : null}

      <footer className={s.footer}>
        <LegalFooterLinks />
      </footer>
    </div>
  );
}
